import React, { useEffect, useState, useRef } from "react";
import { useFeatureGatingManagement } from "./hooks/useFeatureGatingManagement";
import { FEATURES, FeatureGate } from "../../lib/utils/featureAccess";
import DataTable from "../../lib/components/DataTable";
import { Edit, Lock, Unlock, Trash2, Plus, SlidersHorizontal } from "lucide-react";
import { Button, Box } from "@mui/material";

const FeatureGatingManagement = () => {
  const {
    features,
    loading,
    selectedFeature,
    fetchFeatures,
    createOrUpdateFeature,
    updateGatingStatus,
    deleteFeature,
    selectFeature,
    clearError,
  } = useFeatureGatingManagement();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    featureName: "",
    featureId: "",
    gated: false,
    allowedUsers: "",
    deniedUsers: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!hasFetched.current) {
      fetchFeatures();
      hasFetched.current = true;
    }
  }, [fetchFeatures]);

  useEffect(() => {
    if (selectedFeature) {
      setFormData({
        featureName: selectedFeature.featureName || "",
        featureId: selectedFeature.featureId || "",
        gated: selectedFeature.gated || false,
        allowedUsers: selectedFeature.allowedUsers?.join(", ") || "",
        deniedUsers: selectedFeature.deniedUsers?.join(", ") || "",
      });
    }
  }, [selectedFeature]);

  const columns = [
    { key: "featureName", label: "Feature Name", sortable: true },
    { key: "featureId", label: "Feature ID", sortable: true },
    {
      key: "gated",
      label: "Gated",
      sortable: true,
      render: value => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            value ? "bg-accent-light text-accent-dark" : "bg-green-100 text-green-800"
          }`}
        >
          {value ? "Gated" : "Open"}
        </span>
      ),
    },
    {
      key: "allowedUsers",
      label: "Allowed Users",
      sortable: false,
      render: value => <span className="text-sm">{Array.isArray(value) ? value.length : 0} users</span>,
    },
    {
      key: "deniedUsers",
      label: "Denied Users",
      sortable: false,
      render: value => <span className="text-sm">{Array.isArray(value) ? value.length : 0} users</span>,
    },
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      filterable: false,
      render: (value, row) => (
        <div className="flex gap-2">
          <FeatureGate featureId={FEATURES.SYSTEM_SETTINGS}>
            <button
              onClick={() => handleEditFeature(row)}
              className="p-2 rounded hover:bg-gray-100 text-gray-600 hover:text-accent transition-colors"
              title="Edit Feature"
            >
              <Edit size={16} />
            </button>
            <button
              onClick={async () => {
                if (!row || !row.featureId) return;
                try {
                  await updateGatingStatus(row.featureId, !row.gated);
                } catch {
                  // Gating status update failed
                }
              }}
              className="p-2 rounded hover:bg-gray-100 text-gray-600 hover:text-blue-600 transition-colors"
              title={row.gated ? "Open Feature" : "Gate Feature"}
            >
              {row.gated ? <Unlock size={16} /> : <Lock size={16} />}
            </button>
            <button
              onClick={async () => {
                if (!row || !row.featureId) return;
                if (window.confirm(`Are you sure you want to delete feature ${row.featureName}?`)) {
                  try {
                    await deleteFeature(row.featureId);
                  } catch {
                    // Feature deletion failed
                  }
                }
              }}
              className="p-2 rounded hover:bg-gray-100 text-gray-600 hover:text-red-600 transition-colors"
              title="Delete Feature"
            >
              <Trash2 size={16} />
            </button>
          </FeatureGate>
        </div>
      ),
    },
  ];

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        allowedUsers: formData.allowedUsers
          ? formData.allowedUsers
              .split(",")
              .map(s => s.trim())
              .filter(Boolean)
          : [],
        deniedUsers: formData.deniedUsers
          ? formData.deniedUsers
              .split(",")
              .map(s => s.trim())
              .filter(Boolean)
          : [],
      };
      if (selectedFeature) {
        await createOrUpdateFeature(payload.featureName, payload);
        selectFeature(null);
        setShowCreateModal(false);
      } else {
        await createOrUpdateFeature(payload.featureName, payload);
        setShowCreateModal(false);
      }
      setFormData({ featureName: "", featureId: "", gated: false, allowedUsers: "", deniedUsers: "" });
      clearError();
      fetchFeatures();
    } catch {
      // Feature save failed
    }
  };

  const handleInputChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleEditFeature = feature => {
    if (feature && feature.featureId) {
      selectFeature(feature);
      setShowCreateModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    selectFeature(null);
    setFormData({ featureName: "", featureId: "", gated: false, allowedUsers: "", deniedUsers: "" });
  };

  return (
    <FeatureGate featureId={FEATURES.SYSTEM_SETTINGS}>
      <div>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1.5, mb: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setShowFilters(prev => !prev)}
            startIcon={<SlidersHorizontal size={15} />}
            sx={{
              textTransform: "none",
              borderColor: showFilters ? "var(--color-accent-active)" : "#d1d5db",
              borderRadius: "10px",
              color: showFilters ? "var(--color-accent-active)" : "#374151",
              backgroundColor: showFilters ? "var(--color-accent-alpha-06)" : "transparent",
              fontWeight: 600,
              fontSize: "0.8rem",
              px: 2,
              py: 0.7,
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "var(--color-accent-active)",
                backgroundColor: "var(--color-accent-lightest)",
                color: "var(--color-accent-active)",
              },
            }}
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
          <FeatureGate featureId={FEATURES.SYSTEM_SETTINGS}>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 btn-accent px-4 py-2 rounded-lg transition-colors shadow-sm"
            >
              <Plus size={16} />
              Add Feature
            </button>
          </FeatureGate>
        </Box>

        <DataTable columns={columns} data={features} loading={loading} showFilters={showFilters} pageSize={10} />

        {/* Create/Edit Feature Modal */}
        {(showCreateModal || selectedFeature) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-bold mb-4">{selectedFeature ? "Edit Feature" : "Create Feature"}</h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="featureName" className="block text-sm font-medium text-gray-700 mb-1">
                    Feature Name *
                  </label>
                  <input
                    id="featureName"
                    type="text"
                    name="featureName"
                    value={formData.featureName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-accent"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="featureId" className="block text-sm font-medium text-gray-700 mb-1">
                    Feature ID *
                  </label>
                  <input
                    id="featureId"
                    type="text"
                    name="featureId"
                    value={formData.featureId}
                    onChange={handleInputChange}
                    disabled={!!selectedFeature}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-accent"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="gated" className="flex items-center space-x-2">
                    <input
                      id="gated"
                      type="checkbox"
                      name="gated"
                      checked={formData.gated}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-accent border-gray-300 rounded focus:ring-accent"
                    />
                    <span className="text-sm font-medium text-gray-700">Gated Feature</span>
                  </label>
                </div>

                <div>
                  <label htmlFor="allowedUsers" className="block text-sm font-medium text-gray-700 mb-1">
                    Allowed Users (comma-separated)
                  </label>
                  <textarea
                    id="allowedUsers"
                    name="allowedUsers"
                    value={formData.allowedUsers}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-accent"
                    placeholder="user1@example.com, user2@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="deniedUsers" className="block text-sm font-medium text-gray-700 mb-1">
                    Denied Users (comma-separated)
                  </label>
                  <textarea
                    id="deniedUsers"
                    name="deniedUsers"
                    value={formData.deniedUsers}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-accent"
                    placeholder="user1@example.com, user2@example.com"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="px-4 py-2 btn-accent-submit rounded-md">
                    {selectedFeature ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </FeatureGate>
  );
};

export default FeatureGatingManagement;
